import type { Booking } from '@/types';

export function parseStoredBookingTime(value: string): Date {
  return new Date(value.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, ''));
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_HOURLY_MS = 30 * 60 * 1000;

/**
 * Full-day bookings are stored with an inclusive end (23:59:59.999) while the
 * timeline resizes against exclusive midnight boundaries, so a whole-day drag
 * arrives one millisecond short. Snapping near-whole-day deltas keeps every
 * booking in the group on the same day boundaries as the resized one.
 */
export function quantizeResizeDelta(delta: number): number {
  const days = Math.round(delta / DAY_MS);
  if (days === 0) return delta;
  const snapped = days * DAY_MS;
  return Math.abs(delta - snapped) <= 1000 ? snapped : delta;
}

export interface GroupResizeTarget {
  booking: Booking;
  newStartTime: Date;
  newEndTime: Date;
}

export function groupResizeTargets({
  resized,
  selection,
  correctedStart,
  correctedEnd,
}: {
  resized: Booking;
  selection: Booking[];
  correctedStart: Date;
  correctedEnd: Date;
}): { targets: GroupResizeTarget[]; skipped: Booking[] } {
  const orderedSelection = [
    resized,
    ...selection.filter(booking => String(booking.id) !== String(resized.id)),
  ];

  if (resized.recurrenceGroupId) {
    return { targets: [], skipped: orderedSelection };
  }

  const resizedStart = parseStoredBookingTime(resized.startTime);
  const resizedEnd = parseStoredBookingTime(resized.endTime);
  const hasValidResizedRange =
    Number.isFinite(resizedStart.getTime()) &&
    Number.isFinite(resizedEnd.getTime());
  const startDelta = correctedStart.getTime() - resizedStart.getTime();
  const endDelta = correctedEnd.getTime() - resizedEnd.getTime();

  if (
    !hasValidResizedRange ||
    !Number.isFinite(startDelta) ||
    !Number.isFinite(endDelta)
  ) {
    return { targets: [], skipped: [] };
  }

  const startEdgeMoved = Math.abs(startDelta) > Math.abs(endDelta);
  const delta = quantizeResizeDelta(startEdgeMoved ? startDelta : endDelta);
  if (delta === 0) return { targets: [], skipped: [] };

  const targets: GroupResizeTarget[] = [];
  const skipped: Booking[] = [];

  for (const booking of orderedSelection) {
    if (booking.recurrenceGroupId) {
      skipped.push(booking);
      continue;
    }

    const originalStart = parseStoredBookingTime(booking.startTime);
    const originalEnd = parseStoredBookingTime(booking.endTime);
    const hasValidRange =
      Number.isFinite(originalStart.getTime()) &&
      Number.isFinite(originalEnd.getTime());
    let newStartTime = startEdgeMoved
      ? new Date(originalStart.getTime() + delta)
      : originalStart;
    let newEndTime = startEdgeMoved
      ? originalEnd
      : new Date(originalEnd.getTime() + delta);

    if (
      !hasValidRange &&
      String(booking.id) !== String(resized.id)
    ) {
      skipped.push(booking);
      continue;
    }

    if (String(booking.id) !== String(resized.id)) {
      const originalDuration = originalEnd.getTime() - originalStart.getTime();
      const unit = booking.durationType === 'hourly' ? MIN_HOURLY_MS : DAY_MS - 1;
      const minDuration = Math.min(originalDuration, unit);

      if (startEdgeMoved) {
        newStartTime = new Date(Math.min(
          newStartTime.getTime(),
          newEndTime.getTime() - minDuration,
        ));
      } else {
        newEndTime = new Date(Math.max(
          newEndTime.getTime(),
          newStartTime.getTime() + minDuration,
        ));
      }
    }

    targets.push({ booking, newStartTime, newEndTime });
  }

  return { targets, skipped };
}
